import {
  ApiError,
  BadgeService,
  CardService,
  EmployeeService,
  GratitudeService,
  LoginService,
  TeamService,
  UserService,
} from 'client';
import {OpenAPI} from 'client/core/OpenAPI';
import {CurrentEnvironment, getCurrentEnvironment} from 'helpers/environment';

import standardConfig from './config.json';
import prodConfig from './config.prod.json';
import stagingConfig from './config.staging.json';

type AppConfig = typeof standardConfig;

const withTrailingSlash = (value: string) => (value.endsWith('/') ? value : `${value}/`);

const withoutTrailingSlash = (value: string) => value.replace(/\/+$/, '');

const AURA_VIDEO_PASTE_HOSTNAMES = [
  'aura-dev.corp.dropbox.com',
  'aura-test.corp.dropbox.com',
  'aura.corp.dropbox.com',
] as const;

function resolveAuraPublicOrigin(): string {
  switch (getCurrentEnvironment()) {
    case CurrentEnvironment.Development:
      return withoutTrailingSlash('https://aura-dev.corp.dropbox.com');
    case CurrentEnvironment.Staging:
    case CurrentEnvironment.Amplify:
      return withoutTrailingSlash('https://aura-test.corp.dropbox.com');
    case CurrentEnvironment.Production:
      return withoutTrailingSlash('https://aura.corp.dropbox.com');
  }
}

export const getAuraPasteAllowedHostnames = (): readonly string[] => AURA_VIDEO_PASTE_HOSTNAMES;

const buildLoginUrl = (authBackendUrl: string, frontendUrl: string) => {
  const loginUrl = new URL('/api/v1/auth/login', withTrailingSlash(authBackendUrl));
  loginUrl.searchParams.set('redirect_uri', frontendUrl);
  return loginUrl.toString();
};

const buildLogoutUrl = (authBackendUrl: string, frontendUrl: string) => {
  const logoutUrl = new URL('/api/v1/auth/logout', withTrailingSlash(authBackendUrl));
  logoutUrl.searchParams.set('post_logout_redirect_uri', new URL('login', frontendUrl).toString());
  return logoutUrl.toString();
};

const baseConfig =
  getCurrentEnvironment() === CurrentEnvironment.Development
    ? {...standardConfig}
    : getCurrentEnvironment() === CurrentEnvironment.Production
      ? {...prodConfig}
      : {...stagingConfig};

const frontendUrl = withTrailingSlash(import.meta.env.VITE_FRONTEND_URL || baseConfig.FRONTEND_URL);
const backendUrl = withoutTrailingSlash(import.meta.env.VITE_BACKEND_URL || baseConfig.BACKEND_URL);
const authBackendUrl = withoutTrailingSlash(
  import.meta.env.VITE_AUTH_V1_BACKEND_URL || baseConfig.AUTH_V1_BACKEND_URL || backendUrl
);

const configData: AppConfig = {
  ...baseConfig,
  FRONTEND_URL: frontendUrl,
  BACKEND_URL: backendUrl,
  AUTH_V1_BACKEND_URL: authBackendUrl,
  LOGIN_URL: import.meta.env.VITE_LOGIN_URL || buildLoginUrl(authBackendUrl, frontendUrl),
  LOGOUT_URL: import.meta.env.VITE_LOGOUT_URL || buildLogoutUrl(authBackendUrl, frontendUrl),
  WAITLIST_URL: import.meta.env.VITE_WAITLIST_URL || baseConfig.WAITLIST_URL,
  COLLABORATION_URL: import.meta.env.VITE_COLLABORATION_URL || baseConfig.COLLABORATION_URL,
};

OpenAPI.BASE = configData.AUTH_V1_BACKEND_URL;
OpenAPI.CREDENTIALS = 'include';
OpenAPI.WITH_CREDENTIALS = true;

export const getLoginUrl = () => {
  return configData.LOGIN_URL;
};

export const getLogoutUrl = () => {
  return configData.LOGOUT_URL;
};

export const getFrontendUrl = () => {
  return configData.FRONTEND_URL;
};

export const getBackendUrl = () => {
  return configData.BACKEND_URL;
};

export const getWaitlistUrl = () => {
  return configData.WAITLIST_URL;
};

export const getCollaborationUrl = () => {
  return configData.COLLABORATION_URL;
};

export const getAuraPublicOrigin = (): string => resolveAuraPublicOrigin();

export const getProfileImageUrl = (ldap: string) => {
  return `/api/v1/images/${ldap}.jpg`;
};

export const emailToLdap = (email?: string) => email?.split('@')[0] ?? '';

export const nameToInitials = (name: string) => {
  return name
    ?.split(' ')
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();
};

export const getService = <T,>(ServiceClass: T): T => {
  OpenAPI.BASE = configData.AUTH_V1_BACKEND_URL;
  OpenAPI.CREDENTIALS = 'include';
  OpenAPI.WITH_CREDENTIALS = true;
  return ServiceClass;
};

export const getAuthService = () => {
  OpenAPI.BASE = configData.AUTH_V1_BACKEND_URL;
  OpenAPI.CREDENTIALS = 'include';
  OpenAPI.WITH_CREDENTIALS = true;
  return UserService;
};

export const getLoginService = () => {
  OpenAPI.BASE = configData.AUTH_V1_BACKEND_URL;
  OpenAPI.CREDENTIALS = 'include';
  OpenAPI.WITH_CREDENTIALS = true;
  return LoginService;
};

export const getEmployeeService = () => {
  OpenAPI.BASE = configData.BACKEND_URL;
  OpenAPI.CREDENTIALS = 'include';
  OpenAPI.WITH_CREDENTIALS = true;
  return EmployeeService;
};

export const getTeamsService = () => {
  OpenAPI.BASE = configData.BACKEND_URL;
  OpenAPI.CREDENTIALS = 'include';
  OpenAPI.WITH_CREDENTIALS = true;
  return TeamService;
};

export const getCardsService = () => {
  OpenAPI.BASE = configData.BACKEND_URL;
  OpenAPI.CREDENTIALS = 'include';
  OpenAPI.WITH_CREDENTIALS = true;
  return CardService;
};

export const getGratitudesService = () => {
  OpenAPI.BASE = configData.BACKEND_URL;
  OpenAPI.CREDENTIALS = 'include';
  OpenAPI.WITH_CREDENTIALS = true;
  return GratitudeService;
};

export const getBadgesService = () => {
  return BadgeService;
};

export const isUserDeactivatedApiError = (error: ApiError) => {
  return error.body?.detail === 'User is deactivated';
};

export const isCollabServerEnabledForPage = (pageKey: string) => {
  if (pageKey.startsWith('/workstreams/checkup')) {
    return false;
  }
  return (
    pageKey.startsWith('workstreams') ||
    pageKey.startsWith('planning') ||
    pageKey.startsWith('scorecards')
  );
};

export const getTimezoneOffset = (timeZone: string): number => {
  // If the current user is in California, and the timeZone passed in is America/New_York,
  // this will return `3` indicating the other user is 3 hours ahead (of California)

  const myNow = new Date();
  myNow.setMilliseconds(0);
  const theirNow = new Date(myNow.toLocaleString('en-US', {timeZone}));
  const offsetMinutes = (theirNow.getTime() - myNow.getTime()) / 1000 / 60;
  return offsetMinutes / 60;
};
